import React from 'react';

import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';

import {RecordingEventMetadata, RecordingMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {REPLAY_EVENT} from './query-utils';
import {getSearchHighlightPieces} from './util';

type ReplayPlaylistRowEventMetadataProps = {
  loadingEventMetadata: boolean;
  replay: RecordingMetadata;
  getDisplayTime: (time: number) => string;
  jumpToEvent: (time: number, replayId: string) => void;
  currentRecordingProgressTime: number;
  searchFilter: string;
};

function replayPlaylistRowEventMetadata(props: ReplayPlaylistRowEventMetadataProps) {
  const getSelectedEventIndex = (sortedEventMetadata: RecordingEventMetadata[]) => {
    if (!props.currentRecordingProgressTime) {
      return -1;
    }

    let minDistance = Number.POSITIVE_INFINITY;
    let selectedIndex = -1;

    sortedEventMetadata.forEach((event, i) => {
      const distance = Math.abs(event.eventTime - props.currentRecordingProgressTime);
      if (distance < minDistance) {
        selectedIndex = i;
        minDistance = distance;
      }
    });

    return selectedIndex;
  };

  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);

    return (
      <>
        {pieces.map((piece) => {
          return <span className={piece.highlight ? styles.replaysTextHighlight : ``}>{piece.text}</span>;
        })}
      </>
    );
  }

  const existingEventMetadata = props.replay.replayEvents || [];

  let sortedEventMetadata = existingEventMetadata
    .sort((a, b) => a.eventTime - b.eventTime)
    .filter((event) => event.eventName !== REPLAY_EVENT);

  if (props.searchFilter) {
    sortedEventMetadata = sortedEventMetadata.filter((event) => {
      return getSearchHighlightPieces(event.eventName, props.searchFilter).some((piece) => piece.highlight);
    });
  }

  const selectedEventIndex = getSelectedEventIndex(sortedEventMetadata);

  return (
    <div className={styles.replaysListItemEventExpansion}>
      {props.loadingEventMetadata ? (
        <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Small} role={`progressbar`} />
      ) : (
        <div className={styles.replaysListItemEventBody}>
          {sortedEventMetadata.length === 0 && (
            <div className={styles.replaysListItemEventHeaderEmpty}>No Related Events Found</div>
          )}
          {sortedEventMetadata.map((event, i) => {
            return (
              <div
                key={i}
                className={`${styles.replaysListItemEvent} ${
                  selectedEventIndex === i && styles.replaysListItemEventTimeSelected
                }`}
                onClick={() => {
                  props.jumpToEvent(event.eventTime, props.replay.replayId);
                }}
              >
                <div className={styles.replaysListItemEventFirst}>
                  <div className={styles.replaysListItemEventTime}>{props.getDisplayTime(event.eventTime)}</div>
                  <div className={styles.replaysListItemEventName}>{maybeShowSearchHighlight(event.eventName)}</div>
                </div>
                <div className={styles.replaysListItemEventQuantity}>{event.quantity}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ReplayPlaylistRowEventMetadataStyled = withStyles(replayPlaylistRowEventMetadata, css);
export const ReplayPlaylistRowEventMetadataEl = react2panel(
  `mp-replay-playlist-row-event-metadata`,
  ReplayPlaylistRowEventMetadataStyled,
);
